import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    IconButton,
    Paper,
    Grid,
    Dialog,
    DialogContent,
    CircularProgress
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import ImageIcon from '@mui/icons-material/Image';
import { getPhotosAndAlbums } from '../../../../Utils/apiService';
import routes from '../../../../Utils/routes';
import KenLoader from '../../../../GlobalComponents/KenLoader';

interface FileRecord {
    Id: string;
    Name: string;
    File_Link__c: string;
    CreatedDate: string;
    File_Admin__r?: {
        Profile_Picture_URL__c: string;
    };
}

interface FolderData {
    Id: string;
    Name: string;
    Folder_Type__c: string;
    Folder_Access__c: string;
    CreatedDate: string;
    LastModifiedDate: string;
    Files__r?: {
        totalSize: number;
        records: FileRecord[];
    };
}

const AlbumViewer: React.FC = () => {
    const { folderId } = useParams<{ folderId: string }>();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [albumData, setAlbumData] = useState<FolderData | null>(null);
    const [images, setImages] = useState<FileRecord[]>([]);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [sliderOpen, setSliderOpen] = useState(false);
    const [photosData, setPhotosData] = useState<any[]>([]);
    const [albumsData, setAlbumsData] = useState<any[]>([]);
    const [imageLoading, setImageLoading] = useState(false);

    useEffect(() => {
        console.log("Folder ID from URL params:", folderId);

        // Existing fetch code...
    }, [folderId]);

    useEffect(() => {
        // Fetch album data based on folderId
        const fetchAlbumData = async () => {
            try {
                setLoading(true);
                // Replace the fetch with the imported API function
                const response = await getPhotosAndAlbums();
                console.log("API Response:", response);

                // Find the matching album from the response
                let foundAlbum = null;
                if (response && response.success) {
                    console.log("All albums:", response.data.data.albums);
                    foundAlbum = response.data.data.albums.find((album: FolderData) => album.Id === folderId);
                    console.log("Found album:", foundAlbum);
                }

                if (foundAlbum) {
                    setAlbumData(foundAlbum);

                    // Filter for valid images (with valid File_Link__c)
                    if (foundAlbum.Files__r && foundAlbum.Files__r.records) {
                        console.log("All files in album:", foundAlbum.Files__r.records);
                        const validImages = foundAlbum.Files__r.records.filter((file: { File_Link__c: string; }) =>
                            file.File_Link__c &&
                            file.File_Link__c.startsWith('http') &&
                            !file.File_Link__c.includes('Insert failed')
                        );
                        setImages(validImages);
                    }
                } else {
                    console.error("Album not found with ID:", folderId);
                }
            } catch (error) {
                console.error("Error fetching album data:", error);
            } finally {
                setLoading(false);
            }
        };

        if (folderId) {
            fetchAlbumData();
        }
    }, [folderId]);

    useEffect(() => {
        // Fetch all photos and albums for the upload navigation
        const fetchAllPhotosAndAlbums = async () => {
            try {
                const response = await getPhotosAndAlbums();
                if (response && response.data && response.data.data) {
                    setPhotosData(response.data.data.photos || []);
                    setAlbumsData(response.data.data.albums || []);
                }
            } catch (error) {
                console.error("Error fetching all photos and albums:", error);
            }
        };

        fetchAllPhotosAndAlbums();
    }, []);

    const handleAddPhotos = () => {
        const albumsInfo = albumsData.map(album => ({
            id: album.Id,
            name: album.Name,
        }));

        const photosInfo = photosData.map(photo => ({
            id: photo.Id,
            name: photo.Name,
            fileLink: photo.File_Link__c,
            description: photo.Description__c,
            location: photo.Location__c,
            createdDate: photo.CreatedDate
        }));

        navigate(routes.PhotoAlbumUpload, {
            state: {
                albums: albumsInfo,
                photos: photosInfo,
                currentAlbumId: folderId,
                selectedAlbum: folderId
            }
        });
    };

    const openSlider = (index: number) => {
        setCurrentImageIndex(index);
        setSliderOpen(true);
    };

    const closeSlider = () => {
        setSliderOpen(false);
    };

    const nextImage = () => {
        setImageLoading(true);
        setCurrentImageIndex((prevIndex) =>
            prevIndex === images.length - 1 ? 0 : prevIndex + 1
        );
    };

    const prevImage = () => {
        setImageLoading(true);
        setCurrentImageIndex((prevIndex) =>
            prevIndex === 0 ? images.length - 1 : prevIndex - 1
        );
    };

    if (loading) {
        return (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                sx={{
                    height: '83.5vh',
                    backgroundColor: '#fff',
                    borderRadius: '9px',
                    width: '100%'
                }}
            >
                <KenLoader />
            </Box>
        );
    }

    return (
        <Paper
            elevation={0}
            sx={{
                backgroundColor: '#fff',
                borderRadius: '9px',
                height: '83.5vh',
                width: '100%',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                '@media (min-width: 1280px) and (max-width: 1300px)': {
                    height: '81vh',
                },
                '@media (max-width: 1600px)': {
                    height: '83.5vh',
                },
                '@media (max-width: 1360px)': {
                    height: '100vh',
                }
            }}
        >
            <Box display="flex" alignItems="center" p={3} pb={2}>
                <IconButton
                    onClick={() => navigate(-1)}
                    sx={{ color: '#123A6F', mr: 1 }}
                >
                    <ArrowBackIcon />
                </IconButton>
                <Typography variant="h5" fontWeight={500} color="#123A6F" sx={{ flex: 1 }}>
                    {albumData?.Name || 'Album'}
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleAddPhotos}
                    sx={{
                        backgroundColor: '#123A6F',
                        '&:hover': { backgroundColor: '#0A2851' },
                        borderRadius: 2,
                        textTransform: 'none',
                        px: 3,
                        py: 1,
                        fontSize: '14px',
                        fontWeight: 500,
                        ml: 2
                    }}
                >
                    Add more photos
                </Button>
            </Box>

            <Box
                sx={{
                    flex: 1,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    p: 3,
                    pt: 0
                }}
                className="scrollbar-cus"
            >
                {images.length === 0 ? (
                    <Box
                        display="flex"
                        flexDirection="column"
                        justifyContent="center"
                        alignItems="center"
                        sx={{
                            height: 'calc(100% - 20px)',
                            textAlign: 'center',
                            padding: 3
                        }}
                    >
                        <ImageIcon sx={{ fontSize: 80, color: '#123A6F', opacity: 0.6, mb: 2 }} />
                        <Typography variant="h4" color="#373A45" mb={1}>
                            No images available
                        </Typography>
                        <Typography variant="body2" color="#6F7787" mb={3}>
                            Start by uploading your first image
                        </Typography>
                    </Box>
                ) : (
                    <>
                        <Paper
                            elevation={0}
                            sx={{
                                p: 3,
                                backgroundColor: "#FFFFFF",
                                borderRadius: 3
                            }}
                        >
                            <Grid container spacing={2}>
                                {images.map((image, index) => (
                                    <Grid item xs={12} sm={6} md={4} lg={3} key={image.Id}>
                                        <Paper
                                            elevation={2}
                                            sx={{
                                                borderRadius: 2,
                                                overflow: 'hidden',
                                                transition: 'transform 0.2s',
                                                cursor: 'pointer',
                                                '&:hover': {
                                                    transform: 'scale(1.03)',
                                                    boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                                                }
                                            }}
                                            onClick={() => openSlider(index)}
                                        >
                                            <Box
                                                component="img"
                                                src={image.File_Link__c}
                                                alt={image.Name}
                                                sx={{
                                                    width: '100%',
                                                    height: 220,
                                                    objectFit: 'cover',
                                                }}
                                                onError={(e) => {
                                                    const target = e.target as HTMLImageElement;
                                                    target.style.display = 'none';
                                                    const parent = target.parentElement;
                                                    if (parent) {
                                                        const fallbackElement = document.createElement('div');
                                                        fallbackElement.style.width = '100%';
                                                        fallbackElement.style.height = '220px';
                                                        fallbackElement.style.display = 'flex';
                                                        fallbackElement.style.justifyContent = 'center';
                                                        fallbackElement.style.alignItems = 'center';
                                                        fallbackElement.style.backgroundColor = '#F0F4F8';

                                                        const iconElement = document.createElement('div');
                                                        iconElement.innerHTML = `<svg style="width: 64px; height: 64px; color: #123A6F; opacity: 0.7;" viewBox="0 0 24 24"><path fill="currentColor" d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"></path></svg>`;

                                                        fallbackElement.appendChild(iconElement);
                                                        parent.appendChild(fallbackElement);
                                                    }
                                                }}
                                            />
                                            <Box sx={{ p: 1.5 }}>
                                                <Typography fontWeight={500} color="#373A45" noWrap>
                                                    {image.Name}
                                                </Typography>
                                                <Typography variant="body2" color="#6F7787">
                                                    {new Date(image.CreatedDate).toLocaleDateString()}
                                                </Typography>
                                            </Box>
                                        </Paper>
                                    </Grid>
                                ))}
                            </Grid>
                        </Paper>
                    </>
                )}
            </Box>

            {/* Image Slider Dialog */}
            <Dialog
                open={sliderOpen}
                onClose={closeSlider}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    sx: {
                        bgcolor: 'rgba(0,0,0,0.9)',
                        boxShadow: 'none',
                        height: '90vh',
                        m: 2,
                        borderRadius: 2,
                        overflow: 'hidden'
                    }
                }}
            >
                <DialogContent sx={{ p: 0, position: 'relative', height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Box
                        display="flex"
                        justifyContent="flex-end"
                        p={1}
                        sx={{ position: 'absolute', top: 0, right: 0, zIndex: 10 }}
                    >
                        <IconButton onClick={closeSlider} sx={{ color: 'white' }}>
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    <Box
                        display="flex"
                        flexDirection="column"
                        justifyContent="center"
                        alignItems="center"
                        sx={{
                            flex: 1,
                            position: 'relative'
                        }}
                    >
                        {imageLoading && (
                            <Box position="absolute" display="flex" justifyContent="center" alignItems="center" width="100%" height="100%">
                                <KenLoader />
                            </Box>
                        )}

                        {images.length > 0 && (
                            <Box
                                component="img"
                                src={images[currentImageIndex]?.File_Link__c}
                                alt={images[currentImageIndex]?.Name}
                                sx={{
                                    maxHeight: '80vh',
                                    maxWidth: '100%',
                                    objectFit: 'contain'
                                }}
                                onLoad={() => setImageLoading(false)}
                                onError={(e) => {
                                    setImageLoading(false);
                                    const target = e.target as HTMLImageElement;
                                    target.style.display = 'none';
                                    const parent = target.parentElement;
                                    if (parent) {
                                        const fallbackElement = document.createElement('div');
                                        fallbackElement.style.display = 'flex';
                                        fallbackElement.style.justifyContent = 'center';
                                        fallbackElement.style.alignItems = 'center';
                                        fallbackElement.style.width = '100%';
                                        fallbackElement.style.height = '50vh';

                                        const iconElement = document.createElement('div');
                                        iconElement.innerHTML = `<svg style="width: 96px; height: 96px; color: #FFFFFF; opacity: 0.7;" viewBox="0 0 24 24"><path fill="currentColor" d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"></path></svg>`;

                                        fallbackElement.appendChild(iconElement);
                                        parent.appendChild(fallbackElement);
                                    }
                                }}
                            />
                        )}

                        <Box
                            display="flex"
                            justifyContent="space-between"
                            width="100%"
                            position="absolute"
                            px={2}
                        >
                            {images.length > 1 && (
                                <>
                                    <IconButton
                                        onClick={() => {
                                            setImageLoading(true);
                                            prevImage();
                                        }}
                                        sx={{
                                            color: 'white',
                                            backgroundColor: 'rgba(0,0,0,0.3)',
                                            '&:hover': { backgroundColor: 'rgba(0,0,0,0.5)' }
                                        }}
                                    >
                                        <ChevronLeftIcon fontSize="large" />
                                    </IconButton>

                                    <IconButton
                                        onClick={() => {
                                            setImageLoading(true);
                                            nextImage();
                                        }}
                                        sx={{
                                            color: 'white',
                                            backgroundColor: 'rgba(0,0,0,0.3)',
                                            '&:hover': { backgroundColor: 'rgba(0,0,0,0.5)' }
                                        }}
                                    >
                                        <ChevronRightIcon fontSize="large" />
                                    </IconButton>
                                </>
                            )}
                        </Box>
                    </Box>

                    <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{
                            backgroundColor: 'rgba(0,0,0,0.7)',
                            p: 2,
                            color: 'white'
                        }}
                    >
                        <Typography variant="body1">
                            {images[currentImageIndex]?.Name}
                        </Typography>
                        <Typography variant="body2">
                            {images.length > 1 ? `${currentImageIndex + 1} of ${images.length}` : '1 of 1'}
                        </Typography>
                    </Box>
                </DialogContent>
            </Dialog>
        </Paper>
    );
};

export default AlbumViewer;
